import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userService } from '../services/users';
import { refreshAccessToken, tokenExp, TOKEN_REFRESHED_EVENT } from '../services/sessionRefresh';
import type { User, UserRole, RoleSummary } from '../types';

// Exported so tests can type their useAuth() mocks against the real contract.
// An untyped hand-rolled mock is what silently broke Sidebar.test.tsx when
// hasRoleAssignment was added in V17.128 (see that file's setupAuth).
export interface AuthContextType {
  user: User | null;
  permissions: string[];  // V6.5 Hybrid RBAC
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  // V7.0: Permission-based helpers (preferred)
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  hasAllPermissions: (permissionCodes: string[]) => boolean;
  // V17.128: Role-assignment helper (multi-role users; checks assignment set, not active role)
  hasRoleAssignment: (roleName: string) => boolean;
  // Legacy role-based helpers (DEPRECATED - use hasPermission instead)
  isAdmin: () => boolean;
  isApprover: () => boolean;
  isAROfficer: () => boolean;
  isFinanceAdmin: () => boolean;
  getDefaultRoute: () => string;
  // V17.71: Multi-role support
  needsRoleSelection: boolean;
  availableRoles: RoleSummary[];
  activeRoleName: string | null;
  switchRole: (roleId: string) => Promise<string>;
  // V17.520 (brief 2026-10-26-001, F01): the server could not be reached at start (or since);
  // the cached user and token are in use and the app re-checks by itself.
  isOffline?: boolean;  // optional in the type so existing typed test mocks stay valid; the provider always sets it
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * V7.0 Workspace Smart Redirect Logic
 *
 * Determines the default route for a user based on their role.
 * This implements the "Login Redirector" for the workspace architecture.
 *
 * V15.0 UPDATE: Permission-based routing takes precedence over role-based routing.
 * Scout users are routed based on permissions before checking deprecated role field.
 *
 * Rules:
 * - sales_rep_commercial → /commercial/dashboard (V7.0 Sales Performance Dashboard)
 * - sales_rep_residential → /residential/convert (Sales Workspace)
 * - executive → /analytics (Executive Workspace)
 * - sales_admin_commercial → /dashboard (Admin & Ops Workspace - verification role)
 * - ALL OTHER ROLES → /dashboard (Admin & Ops Workspace)
 *
 * Note: This function is DEPRECATED for new roles. Use permission-based routing in getDefaultRoute() instead.
 */
/**
 * V17.156: Pure role-assignment check, extracted from the AuthProvider
 * closure so it can be unit-tested without an AuthProvider render.
 *
 * Returns true when `roleName` matches the user's currently-active
 * `role_name` OR appears in their full `all_role_names` assignment list.
 * Mirrors backend `user_has_role_assignment` helper (V17.128 PR #1025).
 *
 * Gracefully handles:
 *   - `user === null`                  → false (not logged in)
 *   - `all_role_names === undefined`   → falls back to active `role_name`
 *   - `all_role_names === []`          → falls back to active `role_name`
 *   - exact case-sensitive match (matches backend's `roles.name` column).
 */
export function hasRoleAssignmentForUser(
  user: User | null,
  roleName: string,
): boolean {
  if (!user) return false;
  if (user.role_name === roleName) return true;
  return Array.isArray(user.all_role_names) && user.all_role_names.includes(roleName);
}

/**
 * V17.474 (item 4): HR & Labour holds only crew.attendance.view (+ crew.team_photo.view) — no workspace
 * permission — so, like CS-only users, they would fall through to /dashboard and loop. Land them on the
 * Crew attendance page. Users who also read the fleet (e.g. Head of Service Team) keep their landing.
 */
/**
 * Amendment 2026-10-11-002 (PM 2026-10-11-009 §2, answer A): Head of Service Team and Transport Team
 * Leader land on the D0 board in Today mode. Matched by role name like Sales Manager below. A landing
 * only — no grant: both roles hold logistics.fleet.view, the plans page's and board endpoint's reader.
 */
export const D0_BOARD_TODAY = '/fleet/plans?mode=today';
const CREW_LEADER_ROLES = ['Head of Service Team', 'Transport Team Leader'];

export function crewLeaderLanding(roleName: string | undefined | null): string | null {
  return roleName && CREW_LEADER_ROLES.includes(roleName) ? D0_BOARD_TODAY : null;
}

export function crewAttendanceLanding(hasPermission: (code: string) => boolean): string | null {
  return hasPermission('crew.attendance.view') && !hasPermission('logistics.fleet.view') ? '/fleet/crew-attendance' : null;
}

/**
 * V17.516 (Phase 0, brief 2026-10-25-003 step 8): the ONE default-route resolver.
 * Pure — reads only its arguments — so `getDefaultRoute` (React state) and `switchRole`
 * (fresh API data) cannot drift apart again. It replaces V17.71's separate
 * switchRole copy, which had already lost the crew-leader, CS and crew-attendance rules.
 */
export function resolveDefaultRoute(u: User, perms: readonly string[]): string {
  const hasPerm = (code: string) => perms.includes(code);

  // V16.0: Permission-based routing (PRIORITY ORDER MATTERS)
  // CRITICAL: More specific roles MUST come before general roles to prevent incorrect matching

  // V17.70: Sales Manager → Dedicated Sales Manager Dashboard
  // MUST come FIRST before AR leader check (Sales Manager has ar.dashboard.leader.view for report access,
  // but should NOT be routed to the AR workspace — that's for dedicated AR Team Leaders only)
  // NOTE: Only match by role_name, NOT by approvals.price.discount (shared by Finance Manager, Admin, etc.)
  if (u.role_name === 'Sales Manager') {
    return '/dashboard/sales-manager';
  }

  // Amendment 2026-10-11-002: Head of Service Team / Transport Team Leader → D0 board, Today mode.
  const crewLeader = crewLeaderLanding(u.role_name);
  if (crewLeader) return crewLeader;

  // V17.154: AR Residential default routes — MUST come before commercial AR checks.
  // Residential roles also hold ar.dashboard.{leader,officer}.view (granted by V17.128
  // migration), so the commercial checks below would mis-route them to the commercial
  // workspace. Match by role_name (the active role) so multi-role users land in whichever
  // workspace they currently chose.
  if (u.role_name === 'AR Residential Leader' && !hasPerm('users.edit')) {
    return '/ar/residential/leader';
  }
  if (u.role_name === 'AR Residential Officer' && !hasPerm('users.edit')) {
    return '/ar/residential/officer';
  }
  // V17.505: AR Field Officer → the field map.
  if (u.role_name === 'AR Field Officer' && hasPerm('ar.field.map.view') && !hasPerm('users.edit')) {
    return '/ar/field/map';
  }

  // V17.23.0: AR Commercial Team Leaders → Commercial Command Center
  // MUST come before other checks to ensure AR Leaders land on their workspace
  if (hasPerm('ar.dashboard.leader.view') && !hasPerm('users.edit')) {
    return '/ar-commercial/workspace';  // V17.23.0 AR Commercial Workspace
  }

  // V17.28: AR Commercial Officers → Officer Workspace
  // MUST come before other checks to ensure AR Officers land on their workspace
  // Check both permission AND role_name to catch officers with missing permission seed
  if ((hasPerm('ar.dashboard.officer.view') || u.role_name?.includes('AR') && u.role_name?.includes('Officer')) && !hasPerm('ar.dashboard.leader.view')) {
    return '/ar/commercial/officer';  // V17.28 AR Officer Workspace
  }

  // V17.69: Sales Team Leader - Commercial → Dedicated commercial team dashboard
  // MUST come BEFORE residential.team.view check (this role also has residential.team.view)
  if (u.role_name === 'Sales Team Leader - Commercial' && hasPerm('commercial.leads.assign')) {
    return '/dashboard/sales-team-leader-commercial';
  }

  // V16.0: Residential Team Leaders → Team Command Center
  // IMPORTANT: This MUST come before other residential checks to ensure Team Leaders
  // land on their command center, not the sales rep dashboard
  if (hasPerm('residential.team.view') && !hasPerm('users.edit')) {
    return '/residential/team-dashboard';  // V16.0 Team Leader Command Center
  }

  // V17.64: Admin & Ops users → Admin Dashboard
  // MUST come BEFORE commercial.leads.assign check because admin has ALL permissions
  if (hasPerm('users.edit')) {
    return '/admin/dashboard';
  }

  // V17.65: Sales Admin → Sales Admin Dashboard Hub
  // Sales admins have commercial.onboarding.verify but NOT users.edit
  // MUST come BEFORE commercial.leads.assign check
  if (hasPerm('commercial.onboarding.verify') && !hasPerm('users.edit')) {
    return '/commercial/admin/dashboard';
  }

  // V17.69: Scout Team Leader → Commercial Scout Performance page
  // MUST come BEFORE generic commercial.leads.assign check
  if (u.role_name === 'Scout Team Leader' && hasPerm('scout.lead.allocate')) {
    return '/commercial/scout/performance';
  }

  // V17.63.2: Sales Team Leader → Team Sales Dashboard
  // MUST come before scout check (has both assign AND create permissions)
  if (hasPerm('commercial.leads.assign')) {
    return '/commercial/dashboard';  // V17.63.2 Team Leader starts at team dashboard
  }

  // V16.11: Commercial Scout → My Leads page
  // MUST come BEFORE commercial.pipeline.view check because scouts also have pipeline.view
  // Scouts should see their personal lead portfolio first, not the sales rep pipeline
  if (hasPerm('commercial.leads.create') && hasPerm('commercial.map.view')) {
    return '/commercial/leads';  // V16.11 Scout starts at "My Leads" page
  }

  // Commercial Sales Reps → pipeline view
  // MUST come AFTER scout checks (scouts also have pipeline.view but should go to /commercial/leads)
  if (hasPerm('commercial.pipeline.view')) {
    return '/commercial/pipeline';  // Commercial pipeline workspace
  }

  // Residential Sales Reps → sales rep dashboard (V15.3 "The Scoreboard")
  if (hasPerm('residential.convert.create')) {
    return '/residential/dashboard';  // Residential sales rep dashboard
  }

  // Scout Team Leaders / Key Account Managers → allocation dashboard
  if (hasPerm('scout.lead.allocate')) {
    return '/scout/allocation';  // Manager allocation dashboard
  }

  // Individual Scouts → scout dashboard with stats & targets
  if (hasPerm('scout.lead.create')) {
    return '/scout/dashboard';  // Scout dashboard with stats & targets
  }

  // V17.32.1: Truck Drivers → Driver Route View
  if (hasPerm('operations.driver')) {
    return '/operations/my-route';  // V17.32.1 Driver Route View
  }

  // V17.42: Referrals role → My Referrals Dashboard
  // For referral-only users (not scouts who also have commercial.leads.create)
  if (hasPerm('referrals.create') && !hasPerm('commercial.leads.create')) {
    return '/referrals/my-dashboard';  // V17.42 Referrals Dashboard
  }

  // V17.59.3: TCO (Transport Controller) → Site Check Management
  if (hasPerm('logistics.site_check.manage') && !hasPerm('users.edit')) {
    return '/logistics/site-checks';
  }

  // V17.58.2: Transportation workspace users (TSO) → TSO Dashboard
  if (hasPerm('logistics.dashboard.view') && !hasPerm('users.edit')) {
    return '/logistics/tso-dashboard';
  }

  // V17.246: Customer Service users → CS Dashboard / My Tickets
  // CS-only users (no workspace.*/ar.*/etc. permissions) would otherwise fall through
  // to the role-based fallback below, which defaults to /dashboard — a route gated by
  // hasAnyWorkspaceAccess in Layout.tsx that does NOT include cs.* permissions, causing
  // a redirect loop / blank page. Route them into the CS module directly instead.
  if (hasPerm('cs.tickets.view')) {
    return '/customer-service/dashboard';
  }
  if (hasPerm('cs.tickets.create') || hasPerm('cs.tickets.manage') || hasPerm('cs.admin.manage')) {
    return '/customer-service/my-tickets';
  }

  // V17.474 (item 4): HR & Labour → Crew attendance (see crewAttendanceLanding).
  const crewLanding = crewAttendanceLanding(hasPerm);
  if (crewLanding) return crewLanding;

  // Fall back to role-based routing (deprecated)
  return getDefaultRouteForRole(u.role);
}

export function getDefaultRouteForRole(role: UserRole): string {
  switch (role) {
    case 'sales_rep_commercial':
      return '/commercial/pipeline';  // V15.2: Commercial Sales Pipeline
    case 'sales_rep_residential':
      return '/residential/dashboard';  // V15.3: Residential Sales Dashboard
    case 'team_leader':
      return '/residential/team-dashboard';  // V16.0: Team Leader Command Center
    case 'executive':
      return '/analytics';  // Executive Workspace - analytics and reports
    case 'tso_commercial':
      return '/logistics/tso-dashboard';  // V16.1 TSO Dashboard (Transportation Support)
    case 'tso_residential':
      return '/logistics/tso-dashboard';  // V16.1 TSO Dashboard (Transportation Support)
    case 'transport_commercial_planner':
      return '/logistics/tso-dashboard';  // V17.59.2 Transport Commercial Planner
    // All other roles go to the main dashboard (Admin & Ops Workspace)
    default:
      return '/dashboard';
  }
}

/**
 * V17.520 (Phase 0c, brief 2026-10-26-001, F01): when the boot `/users/me` call fails.
 *
 * Only an answer from the server that rejects the token — HTTP 401 or 403 — logs the user out.
 * A network error, no answer within BOOT_ME_TIMEOUT_MS, a 5xx or any other failure keeps the
 * token: the cached user (`current_user`) and the token's permissions are restored and the
 * session is marked offline. Every API call is still checked by the backend.
 */
export const BOOT_ME_TIMEOUT_MS = 8000;
export const OFFLINE_RECHECK_MS = 30000;
/** Fired by the offline banner's Retry button; AuthProvider re-checks `/users/me` on it. */
export const AUTH_RETRY_EVENT = 'vtw:auth-retry';

export function isTokenRejected(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`no answer within ${ms} ms`)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

/** The token's permissions, role and role_name (the JWT is the source of truth for these). */
function decodeToken(token: string | null): { permissions: string[]; role?: string; role_name?: string } | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { permissions: payload.permissions || [], role: payload.role, role_name: payload.role_name };
  } catch (e) {
    console.error('Failed to decode token:', e);
    return null;
  }
}

/** V17.534 (RBAC 2A): renew this many seconds before the access token's `exp`. */
export const RENEW_BEFORE_EXP_S = 60;
/** V17.534: after a network / 5xx failure, try the renewal again after this long. */
const RENEW_RETRY_MS = 30_000;

/** V17.534: the stored access token expires within RENEW_BEFORE_EXP_S (or already has). */
function tokenExpiringSoon(): boolean {
  const exp = tokenExp(localStorage.getItem('access_token'));
  return exp !== null && exp - Date.now() / 1000 <= RENEW_BEFORE_EXP_S;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);  // V6.5 Hybrid RBAC
  const [isLoading, setIsLoading] = useState(true);
  // V17.71: Multi-role state
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<RoleSummary[]>([]);
  const [activeRoleName, setActiveRoleName] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  // V17.534 (RBAC 2A): bumped whenever the stored access token changes (renewed here or in another tab).
  const [tokenTick, setTokenTick] = useState(0);

  // Apply a user (from /users/me, or the cached one when offline) with the token's role data.
  const applyUser = (base: User) => {
    const decoded = decodeToken(localStorage.getItem('access_token'));
    let userWithRole = base;
    if (decoded) {
      setPermissions(decoded.permissions);
      // V16.1: ALWAYS extract role from JWT (source of truth)
      if (decoded.role) userWithRole = { ...userWithRole, role: decoded.role as User['role'] };
      // V17.71: Extract role_name from JWT
      if (decoded.role_name) {
        userWithRole = { ...userWithRole, role_name: decoded.role_name };
        setActiveRoleName(decoded.role_name);
      }
    } else {
      setPermissions([]);
    }
    setUser(userWithRole);
  };

  const refreshRoles = async () => {
    // V17.516 (brief 2026-10-25-003 step 10): a reload used to drop the role
    // switcher — availableRoles was only filled by login/switchRole. Restore it.
    try {
      setAvailableRoles(await userService.getMyRoles());
    } catch {
      // Non-critical — the switcher just won't show
    }
  };

  const fetchMe = () => withTimeout(userService.getCurrentUser(), BOOT_ME_TIMEOUT_MS);

  // F01: re-check while offline — on the browser `online` event, the banner's Retry, and every 30 s.
  useEffect(() => {
    if (!isOffline) return undefined;
    let running = false;
    const recheck = async () => {
      if (running) return;
      running = true;
      try {
        // V17.534 (RBAC 2A): on reconnect, renew an expired access token first, then today's /me.
        if (tokenExpiringSoon()) {
          const renewed = await refreshAccessToken();
          if (renewed.status === 'rejected') {
            setIsOffline(false);
            logout();
            return;
          }
        }
        const currentUser = await fetchMe();
        applyUser(currentUser);
        setIsOffline(false);
        await refreshRoles();
      } catch (error) {
        if (isTokenRejected(error)) {
          setIsOffline(false);
          logout();
        }
      } finally {
        running = false;
      }
    };
    window.addEventListener('online', recheck);
    window.addEventListener(AUTH_RETRY_EVENT, recheck);
    const timer = setInterval(recheck, OFFLINE_RECHECK_MS);
    return () => {
      window.removeEventListener('online', recheck);
      window.removeEventListener(AUTH_RETRY_EVENT, recheck);
      clearInterval(timer);
    };
  }, [isOffline]);

  useEffect(() => {
    // Check if user is already logged in
    const initAuth = async () => {
      if (userService.isAuthenticated()) {
        try {
          // V17.71: Check if JWT is a multi-role pending token
          const token = localStorage.getItem('access_token');
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));

              // Multi-role pending — don't call /me (it will 403)
              if (payload.multi_role) {
                // Restore user from localStorage
                const localUser = userService.getLocalUser();
                if (localUser) {
                  setUser(localUser);
                  setNeedsRoleSelection(true);
                  // Fetch available roles
                  try {
                    const roles = await userService.getMyRoles();
                    setAvailableRoles(roles);
                  } catch {
                    console.error('Failed to fetch roles during init');
                  }
                }
                setIsLoading(false);
                return;
              }
            } catch (e) {
              // Token decode failed, continue with normal flow
            }
          }

          // V17.534 (RBAC 2A): reopening the app after the access token expired — renew it first
          // (online only; offline boot stays as it is), so a user within 14 days is not sent to /login.
          if (tokenExpiringSoon() && navigator.onLine !== false) {
            const renewed = await refreshAccessToken();
            if (renewed.status === 'rejected') {
              userService.logout();
              setIsLoading(false);
              return;
            }
          }

          // V15.0 / V16.1: permissions and role come from the JWT (applyUser), so they are
          // available on every page refresh — and offline, from the cached user (F01).
          const currentUser = await fetchMe();
          applyUser(currentUser);
          await refreshRoles();
        } catch (error) {
          if (isTokenRejected(error)) {
            // The server rejected the token — clear it.
            userService.logout();
          } else {
            // V17.520 (F01): network error, timeout or 5xx — keep the token. With a cached user
            // the app opens offline; without one the login page shows, token untouched.
            const localUser = userService.getLocalUser();
            if (localUser) applyUser(localUser);
            setIsOffline(true);
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // V17.534 (RBAC 2A): follow the stored access token — renewed in this tab or another one.
  useEffect(() => {
    const bump = () => setTokenTick((n) => n + 1);
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'access_token' && e.newValue) bump();
    };
    window.addEventListener(TOKEN_REFRESHED_EVENT, bump);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(TOKEN_REFRESHED_EVENT, bump);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // A renewed token carries the same claims (role, permissions) re-read from the server: apply them.
  useEffect(() => {
    if (tokenTick === 0 || !user || needsRoleSelection) return;
    applyUser(userService.getLocalUser() ?? user);
    // only on a new token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenTick]);

  // V17.534 (RBAC 2A): renew ~60 s before the access token expires. Never while offline: the
  // `online` event renews instead. A rejected renewal (session over) ends the session as a 401 does.
  useEffect(() => {
    if (isLoading) return undefined;
    const exp = tokenExp(localStorage.getItem('access_token'));
    if (exp === null) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onOnline = () => {
      if (!cancelled) void run();
    };
    const run = async () => {
      if (navigator.onLine === false) {
        window.addEventListener('online', onOnline, { once: true });
        return;
      }
      const result = await refreshAccessToken();
      if (cancelled) return;
      if (result.status === 'rejected') {
        logout();
        if (!window.location.pathname.includes('/login')) window.location.href = '/login';
      } else if (result.status === 'offline') {
        window.addEventListener('online', onOnline, { once: true });
      } else if (result.status === 'error') {
        timer = setTimeout(run, RENEW_RETRY_MS);
      }
      // 'ok': TOKEN_REFRESHED_EVENT bumps tokenTick, which re-runs this effect for the new token.
    };
    timer = setTimeout(run, Math.max(0, (exp - RENEW_BEFORE_EXP_S) * 1000 - Date.now()));
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('online', onOnline);
    };
  }, [isLoading, user, tokenTick]);

  const login = async (username: string, password: string) => {
    const response = await userService.login({ username, password });

    // V17.71: Check if multi-role selection is required
    if (response.needs_role_selection && response.available_roles?.length) {
      setUser(response.user);
      setNeedsRoleSelection(true);
      setAvailableRoles(response.available_roles);
      setPermissions([]);
      return;
    }

    // Single-role user — normal flow
    // V16.1: ALWAYS extract role from JWT token (overrides backend user object)
    let userWithRole = response.user;
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role) {
          userWithRole = { ...userWithRole, role: payload.role };
        }
        if (payload.role_name) {
          userWithRole = { ...userWithRole, role_name: payload.role_name };
          setActiveRoleName(payload.role_name);
        }
      } catch (e) {
        console.error('Failed to extract role from JWT:', e);
      }
    }

    setUser(userWithRole);
    setPermissions(response.permissions || []);
    setNeedsRoleSelection(false);
    setAvailableRoles([]);
  };

  /**
   * V17.71: Switch to a different role (for multi-role users).
   * Calls POST /users/select-role, replaces JWT, updates all state.
   * Returns the new default route so the caller can navigate immediately.
   */
  const switchRole = async (roleId: string): Promise<string> => {
    const response = await userService.selectRole(roleId);

    // Extract role info from the new JWT
    let userWithRole = response.user;
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role) {
          userWithRole = { ...userWithRole, role: payload.role };
        }
        if (payload.role_name) {
          userWithRole = { ...userWithRole, role_name: payload.role_name };
          setActiveRoleName(payload.role_name);
        }
      } catch (e) {
        console.error('Failed to extract role from JWT:', e);
      }
    }

    const newPerms = response.permissions || [];

    setUser(userWithRole);
    setPermissions(newPerms);
    setNeedsRoleSelection(false);

    // Refresh available roles (keep for switcher dropdown)
    try {
      const roles = await userService.getMyRoles();
      setAvailableRoles(roles);
    } catch {
      // Non-critical — dropdown just won't show
    }

    // Compute route from fresh data (not stale React state) — the same resolver as getDefaultRoute.
    return resolveDefaultRoute(userWithRole, newPerms);
  };

  const logout = () => {
    setIsOffline(false);
    userService.logout();
    setUser(null);
    setPermissions([]);
    setNeedsRoleSelection(false);
    setAvailableRoles([]);
    setActiveRoleName(null);
  };

  // V7.0: Permission-based helper methods
  const hasPermission = (permissionCode: string): boolean => {
    return permissions.includes(permissionCode);
  };

  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    return permissionCodes.some(code => permissions.includes(code));
  };

  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    return permissionCodes.every(code => permissions.includes(code));
  };

  // V17.128: Role-assignment check (multi-role support). Delegates to the
  // pure `hasRoleAssignmentForUser` helper so the logic is unit-testable
  // outside the AuthProvider closure (V17.156).
  const hasRoleAssignment = (roleName: string): boolean => {
    return hasRoleAssignmentForUser(user, roleName);
  };

  // DEPRECATED: Legacy role-based helpers (use hasPermission instead)
  // These are kept for backward compatibility during V7.0 migration
  const isAdmin = () => {
    return hasPermission('users.edit');  // V7.0: Admin permission check
  };

  const isApprover = () => {
    return hasPermission('customers.approve');  // V7.0: Approver permission check
  };

  const isAROfficer = () => {
    return hasPermission('customers.sync');  // V7.0: AR Officer permission check
  };

  const isFinanceAdmin = () => {
    return hasPermission('pricing.manage');  // V7.0: Finance Admin permission check
  };

  const getDefaultRoute = () => (user ? resolveDefaultRoute(user, permissions) : '/dashboard');

  const value = {
    user,
    permissions,  // V6.5 Hybrid RBAC
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    // V7.0: Permission-based helpers (preferred)
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // V17.128: Role-assignment helper
    hasRoleAssignment,
    // Legacy role-based helpers (DEPRECATED)
    isAdmin,
    isApprover,
    isAROfficer,
    isFinanceAdmin,
    getDefaultRoute,
    // V17.71: Multi-role support
    needsRoleSelection,
    availableRoles,
    activeRoleName,
    switchRole,
    isOffline,  // V17.520 (F01)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
